import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Field} from 'formik';
import {Button, ButtonGroup, Form} from 'react-bootstrap';
import {
    buildQuartzCronFromSimpleModel,
    CRON_DAY_OPTIONS,
    createDefaultSimpleCronModel,
    CronEditorMode,
    CronSimpleType,
    describeSimpleCronModel,
    getCronEditorMode,
    getQuartzCronEditorHint,
    parseQuartzCronToSimpleModel,
    validateQuartzCronForMinuteScheduler,
} from '../../commons/CronUtils';

const SCHEDULE_TYPE_OPTIONS = [
    { value: CronSimpleType.EVERY_MINUTES, label: 'Co N minut' },
    { value: CronSimpleType.DAILY, label: 'Codziennie' },
    { value: CronSimpleType.WEEKDAYS, label: 'Dni robocze' },
    { value: CronSimpleType.WEEKLY, label: 'Wybrane dni tygodnia' },
    { value: CronSimpleType.MONTHLY, label: 'Co miesiąc' },
];

const MODE_LABELS = {
    [CronEditorMode.SIMPLE]: 'Kreator',
    [CronEditorMode.ADVANCED]: 'Quartz cron',
};

const setTouched = (form, fieldName) => {
    form.setFieldTouched(fieldName, true, false);
}

const setCronValue = (form, fieldName, nextValue, syncedValueRef) => {
    syncedValueRef.current = nextValue;
    form.setFieldValue(fieldName, nextValue);
    setTouched(form, fieldName);
}

const deriveSimpleState = (cron) => parseQuartzCronToSimpleModel(cron) || createDefaultSimpleCronModel();

const normalizeNumericInput = (value, min, max, fallback) => {
    if (value === '' || value == null) return fallback;
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
}

const CronFieldRow = ({label, children, className = ''}) => (
    <div className={`cron-simple-row ${className}`.trim()}>
        <div className="cron-simple-row-label">{label}</div>
        <div className="cron-simple-row-content">{children}</div>
    </div>
)

const CronSimpleMode = ({value, onChange}) => {
    const update = (changes) => onChange({ ...value, ...changes });

    const handleTypeChange = (event) => {
        const type = event.target.value;
        const next = { ...value, type };
        if (type === CronSimpleType.WEEKLY && (!next.daysOfWeek || next.daysOfWeek.length === 0)) {
            next.daysOfWeek = ['MON'];
        }
        update(next);
    }

    const toggleDay = (day) => {
        const hasDay = value.daysOfWeek.includes(day);
        const nextDays = hasDay
            ? value.daysOfWeek.filter(item => item !== day)
            : value.daysOfWeek.concat(day);
        if (nextDays.length === 0) return;
        update({ daysOfWeek: nextDays });
    }

    return <div className="cron-editor-panel">
        <CronFieldRow label="Typ harmonogramu" className="mb-3">
            <Form.Control as="select" value={value.type} onChange={handleTypeChange}>
                {SCHEDULE_TYPE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Form.Control>
        </CronFieldRow>

        {value.type === CronSimpleType.EVERY_MINUTES ? (
            <CronFieldRow label="Interwał">
                <div className="cron-inline-fields">
                    <Form.Control
                        className="cron-number-input"
                        type="number"
                        min={1}
                        max={59}
                        value={value.intervalMinutes}
                        onChange={(event) => update({ intervalMinutes: normalizeNumericInput(event.target.value, 1, 59, 1) })}
                    />
                    <span className="cron-inline-label">minut</span>
                </div>
            </CronFieldRow>
        ) : null}

        {value.type !== CronSimpleType.EVERY_MINUTES ? (
            <CronFieldRow label="Czas">
                <div className="cron-time-grid">
                    <div className="cron-compact-input-group cron-time-cell">
                        <span className="cron-inline-caption">Godzina</span>
                        <Form.Control
                            className="cron-number-input"
                            type="number"
                            min={0}
                            max={23}
                            value={value.hour}
                            onChange={(event) => update({ hour: normalizeNumericInput(event.target.value, 0, 23, 0) })}
                        />
                    </div>
                    <div className="cron-compact-input-group cron-time-cell">
                        <span className="cron-inline-caption">Minuta</span>
                        <Form.Control
                            className="cron-number-input"
                            type="number"
                            min={0}
                            max={59}
                            value={value.minute}
                            onChange={(event) => update({ minute: normalizeNumericInput(event.target.value, 0, 59, 0) })}
                        />
                    </div>
                </div>
            </CronFieldRow>
        ) : null}

        {value.type === CronSimpleType.WEEKLY ? (
            <CronFieldRow label="Dni tygodnia" className="mt-3">
                <div className="cron-day-grid">
                    {CRON_DAY_OPTIONS.map(option => (
                        <Form.Check
                            key={option.value}
                            custom={true}
                            type="checkbox"
                            id={`cron-day-${option.value}`}
                            label={option.label}
                            checked={value.daysOfWeek.includes(option.value)}
                            onChange={() => toggleDay(option.value)}
                        />
                    ))}
                </div>
            </CronFieldRow>
        ) : null}

        {value.type === CronSimpleType.MONTHLY ? (
            <CronFieldRow label="Dzień miesiąca" className="mt-3">
                <div className="cron-inline-fields">
                    <Form.Control
                        className="cron-number-input"
                        type="number"
                        min={1}
                        max={31}
                        value={value.dayOfMonth}
                        onChange={(event) => update({ dayOfMonth: normalizeNumericInput(event.target.value, 1, 31, 1) })}
                    />
                </div>
            </CronFieldRow>
        ) : null}
    </div>;
}

const CronAdvancedMode = ({value, onChange, onBlur}) => {
    const warning = validateQuartzCronForMinuteScheduler(value);

    return <div className="cron-editor-panel cron-editor-panel-advanced">
        <Form.Group className="mb-0">
            <label className="cron-advanced-label">Wyrażenie Quartz cron</label>
            <Form.Control
                type="text"
                value={value || ''}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder="np. 0 30 8 ? * MON-FRI"
            />
        </Form.Group>
        <Form.Text className="text-muted d-block">
            Format 6 lub 7 segmentów, wykonywanie z dokładnością do minuty.
        </Form.Text>
        {warning ? <div className="feedback-invalid d-block">{warning}</div> : null}
    </div>;
}

const CronEditorBody = ({field, form, meta}) => {
    const initialMode = getCronEditorMode(field.value);
    const syncedValueRef = useRef(field.value);
    const [mode, setMode] = useState(initialMode);
    const [simpleState, setSimpleState] = useState(() => deriveSimpleState(field.value));

    useEffect(() => {
        if (field.value === syncedValueRef.current) return;
        syncedValueRef.current = field.value;
        const nextMode = getCronEditorMode(field.value);
        setMode(nextMode);
        setSimpleState(deriveSimpleState(field.value));
    }, [field.value]);

    const helperText = useMemo(() => getQuartzCronEditorHint(field.value), [field.value]);
    const simpleSummary = useMemo(() => describeSimpleCronModel(simpleState), [simpleState]);

    const updateSimpleState = (nextState) => {
        setSimpleState(nextState);
        const cron = buildQuartzCronFromSimpleModel(nextState);
        setCronValue(form, field.name, cron, syncedValueRef);
    }

    const handleModeChange = (nextMode) => {
        setMode(nextMode);
        if (nextMode === CronEditorMode.SIMPLE) {
            const nextState = parseQuartzCronToSimpleModel(field.value) || simpleState || createDefaultSimpleCronModel();
            updateSimpleState(nextState);
        } else {
            setTouched(form, field.name);
        }
    }

    return <div className="cron-editor">
        <div className="cron-editor-shell">
            <div className="cron-editor-shell-header">
                <ButtonGroup aria-label="Tryb edycji harmonogramu" className="cron-editor-mode-switch">
                    <Button
                        type="button"
                        variant={mode === CronEditorMode.SIMPLE ? 'primary' : 'light'}
                        className={mode === CronEditorMode.SIMPLE ? 'is-active' : ''}
                        onClick={() => handleModeChange(CronEditorMode.SIMPLE)}
                    >
                        {MODE_LABELS[CronEditorMode.SIMPLE]}
                    </Button>
                    <Button
                        type="button"
                        variant={mode === CronEditorMode.ADVANCED ? 'primary' : 'light'}
                        className={mode === CronEditorMode.ADVANCED ? 'is-active' : ''}
                        onClick={() => handleModeChange(CronEditorMode.ADVANCED)}
                    >
                        {MODE_LABELS[CronEditorMode.ADVANCED]}
                    </Button>
                </ButtonGroup>
            </div>

            <div className="cron-editor-shell-body">
                {mode === CronEditorMode.SIMPLE ? (
                    <CronSimpleMode value={simpleState} onChange={updateSimpleState} />
                ) : (
                    <CronAdvancedMode
                        value={field.value}
                        onChange={(nextValue) => setCronValue(form, field.name, nextValue, syncedValueRef)}
                        onBlur={() => setTouched(form, field.name)}
                    />
                )}
            </div>

            {mode === CronEditorMode.SIMPLE ? (
                <div className="cron-preview-box">
                    <div className="cron-preview-header">
                        <strong>{simpleSummary}</strong>
                    </div>
                    <div className="cron-preview-code">{field.value || '[brak]'}</div>
                    {helperText ? <div className="cron-preview-note">{helperText}</div> : null}
                </div>
            ) : null}
        </div>

        {meta.touched && meta.error ? <div className="feedback-invalid">{meta.error}</div> : null}
    </div>;
}

const CronEditor = ({name}) => (
    <Field name={name}>
        {({field, form, meta}) => <CronEditorBody field={field} form={form} meta={meta} />}
    </Field>
)

export default CronEditor;


